import { useCallback, useEffect, useId, useRef, useState } from "react";

import type { DiagnosticAnswer, DiagnosticContent } from "@/lib/api";
import { cn } from "@/lib/utils";

import { type Band, bandBox, createBand, drawPortrait, targetFor } from "../portrait-draw";
import { answerLevel } from "../scoring";

interface PortraitProps {
  content: DiagnosticContent;
  answers: DiagnosticAnswer[];
  /**
   * True once the interview is over: the portrait gives up height so the index
   * and the gate below it are on screen without a scroll.
   */
  compact: boolean;
  /**
   * True inside a parent page. `vh` there means the height of the frame, which
   * is set from the height of this content, so the portrait is sized in pixels
   * instead to keep the two out of a loop.
   */
  embedded: boolean;
  reducedMotion: boolean;
}

interface Target {
  on: number;
  c: number;
  sp: number;
  score: number;
}

const MAX_DPR = 2;
const FADE_EASE = 0.06;
const POSITION_EASE = 0.05;
/** Keeps the tooltip inside the canvas on narrow screens. */
const TOOLTIP_WIDTH = 260;

/**
 * How tall the canvas is drawn. Every value is written out in full so Tailwind
 * keeps the class; the canvas itself follows through its ResizeObserver, which
 * also re-lays out the band overlays as the height animates.
 */
function boxHeight(compact: boolean, embedded: boolean): string {
  if (embedded) {
    // From 900px the embed is a fixed-height stage: during the interview the
    // portrait takes whatever its column leaves, and the stage is pixels, so
    // this is still no `vh`. Once finished it shrinks to sit above the index.
    return compact
      ? "h-[240px] min-[900px]:h-[260px]"
      : "h-[420px] min-[900px]:h-auto min-[900px]:min-h-0 min-[900px]:flex-1";
  }
  return compact
    ? "h-[320px] min-[900px]:h-[min(40vh,380px)]"
    : "h-[420px] min-[900px]:h-[min(62vh,560px)]";
}

/** The device pixel ratio, capped so a 3x phone does not draw a huge canvas. */
function currentDpr(): number {
  return Math.min(window.devicePixelRatio || 1, MAX_DPR);
}

function trackName(content: DiagnosticContent, track: number): string {
  return content.tracks.find((item) => item.id === track)?.name ?? "";
}

/**
 * The wiring portrait: one band per answer, scattered when the organization is
 * hard-wired and aligned when AI-first behaviour is the default.
 *
 * Every band also gets a transparent, focusable overlay button so keyboard
 * users reach the same track, level and evidence a pointer user sees on hover.
 * The overlays and the tooltip are positioned imperatively through refs, since
 * their geometry comes from the measured canvas height rather than from props.
 */
export function Portrait({ content, answers, compact, embedded, reducedMotion }: PortraitProps) {
  const count = content.questions.length;
  const uid = useId();
  const titleId = `${uid}-portrait-title`;
  const summaryId = `${uid}-portrait-summary`;

  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const overlayRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const bandsRef = useRef<Band[]>([]);
  const targetsRef = useRef<Target[]>([]);
  const hoverRef = useRef(-1);

  const [hoverIndex, setHoverIndex] = useState(-1);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // Re-read on every resize: the ratio changes when the window is zoomed or
  // dragged to a display with a different density, not only at mount.
  const [dpr, setDpr] = useState(currentDpr);

  if (bandsRef.current.length !== count) {
    bandsRef.current = Array.from({ length: count }, (_, index) => createBand(index));
    targetsRef.current = bandsRef.current.map((band) => ({
      on: 0,
      c: band.c,
      sp: band.sp,
      score: 0,
    }));
  }

  // ─── drawing ──────────────────────────────────────────────────────────

  const paint = useCallback(
    (now: number) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) {
        return;
      }
      drawPortrait(ctx, {
        width: size.width,
        height: size.height,
        bands: bandsRef.current,
        hoverIndex: hoverRef.current,
        tracks: content.questions.map((question) => trackName(content, question.track)),
        now,
        reducedMotion,
      });
    },
    [content, reducedMotion, size.height, size.width],
  );

  const settle = useCallback(() => {
    bandsRef.current.forEach((band, index) => {
      const target = targetsRef.current[index];
      if (!target) {
        return;
      }
      band.on = target.on;
      band.c = target.c;
      band.sp = target.sp;
      band.score = target.score;
    });
  }, []);

  // ─── targets follow the answers ───────────────────────────────────────

  useEffect(() => {
    // Derived, not accumulated: when the session resets and `answers` empties,
    // the bands ease back to their unanswered home instead of staying drawn.
    targetsRef.current = Array.from({ length: count }, (_, index) => {
      const answer = answers[index];
      if (!answer) {
        const fresh = createBand(index);
        return { on: 0, c: fresh.c, sp: fresh.sp, score: 0 };
      }
      const { c, sp } = targetFor(index, answer.score);
      return { on: 1, c, sp, score: answer.score };
    });
    if (reducedMotion) {
      settle();
      paint(0);
    }
  }, [answers, count, reducedMotion, settle, paint]);

  // ─── size, device pixel ratio, and the animation loop ─────────────────

  useEffect(() => {
    const box = boxRef.current;
    if (!box) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) {
        return;
      }
      // The height animates when the interview ends, so this fires on every
      // frame of that transition: a size that has not moved is dropped rather
      // than re-rendering the overlays for nothing.
      setSize((current) =>
        current.width === rect.width && current.height === rect.height
          ? current
          : { width: rect.width, height: rect.height },
      );
      setDpr(currentDpr());
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || size.width === 0 || size.height === 0) {
      return;
    }
    canvas.width = Math.round(size.width * dpr);
    canvas.height = Math.round(size.height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paint(performance.now());
  }, [size.height, size.width, dpr, paint]);

  useEffect(() => {
    // With reduced motion there is no drift and no easing, so the portrait only
    // needs a repaint when something actually changes.
    if (reducedMotion) {
      settle();
      paint(0);
      return;
    }
    let frame = 0;
    const loop = (now: number) => {
      bandsRef.current.forEach((band, index) => {
        const target = targetsRef.current[index];
        if (!target) {
          return;
        }
        band.on += (target.on - band.on) * FADE_EASE;
        band.c += (target.c - band.c) * POSITION_EASE;
        band.sp += (target.sp - band.sp) * POSITION_EASE;
        band.score = target.score;
      });
      paint(now);
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [reducedMotion, paint, settle]);

  // ─── overlay geometry ─────────────────────────────────────────────────

  useEffect(() => {
    if (size.height === 0) {
      return;
    }
    overlayRefs.current.forEach((element, index) => {
      if (!element) {
        return;
      }
      const box = bandBox(index, count, size.height);
      element.style.top = `${box.top}px`;
      element.style.height = `${box.height}px`;
    });
  }, [size.height, count, answers.length]);

  const moveTooltip = useCallback(
    (index: number, pointerX?: number) => {
      const tooltip = tooltipRef.current;
      if (!tooltip || size.height === 0) {
        return;
      }
      const box = bandBox(index, count, size.height);
      const maxLeft = Math.max(8, size.width - TOOLTIP_WIDTH - 8);
      const left = Math.min(pointerX ?? 12, maxLeft);
      tooltip.style.left = `${Math.max(8, left)}px`;
      tooltip.style.top = `${box.top + box.height / 2}px`;
    },
    [count, size.height, size.width],
  );

  const showBand = useCallback(
    (index: number, pointerX?: number) => {
      hoverRef.current = index;
      setHoverIndex(index);
      if (index >= 0) {
        moveTooltip(index, pointerX);
      }
    },
    [moveTooltip],
  );

  /** The band under a y offset, or -1 in a gap. Must match what is drawn. */
  function bandAt(y: number): number {
    for (let index = 0; index < count; index += 1) {
      const box = bandBox(index, count, size.height);
      if (y >= box.top && y <= box.top + box.height) {
        return index;
      }
    }
    return -1;
  }

  // Only reached in the gaps between bands and below the last one: an answered
  // band is covered by its overlay button, which tracks the pointer itself.
  function handlePointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas || size.height === 0) {
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const index = bandAt(event.clientY - rect.top);
    showBand(index >= 0 && index < answers.length ? index : -1, event.clientX - rect.left);
  }

  function handleOverlayMove(index: number, event: React.PointerEvent<HTMLButtonElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    showBand(index, event.clientX - rect.left);
  }

  const hovered = hoverIndex >= 0 ? answers[hoverIndex] : undefined;

  return (
    <figure
      aria-labelledby={titleId}
      className={cn(
        embedded &&
          !compact &&
          "min-[900px]:flex min-[900px]:min-h-0 min-[900px]:flex-1 min-[900px]:flex-col",
      )}
    >
      <div className="flex items-baseline justify-between gap-4">
        <span className="hw-mono" id={titleId}>
          Your wiring portrait
        </span>
        <span className="hw-mono">
          {answers.length} / {count}
        </span>
      </div>

      <div
        ref={boxRef}
        className={cn(
          "relative mt-3 w-full",
          boxHeight(compact, embedded),
          !reducedMotion && "ease-hw transition-[height] duration-700",
        )}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-describedby={answers.length > 0 ? summaryId : undefined}
          aria-label={`Your wiring portrait: ${answers.length} of ${count} bands drawn. Bands scatter when your organization is hard-wired and align when AI-first behavior is the default.`}
          className="block h-full w-full"
          onPointerMove={handlePointerMove}
          onPointerLeave={() => showBand(-1)}
        />

        {answers.map((answer, index) => (
          <button
            key={answer.qid}
            type="button"
            ref={(element) => {
              overlayRefs.current[index] = element;
            }}
            onFocus={() => showBand(index)}
            onBlur={() => showBand(-1)}
            onPointerEnter={() => showBand(index)}
            onPointerLeave={() => showBand(-1)}
            onPointerMove={(event) => handleOverlayMove(index, event)}
            aria-label={`${trackName(content, answer.track)}: ${answerLevel(answer.score, content)}. ${answer.evidence ?? ""}`}
            className="focus-visible:outline-hw-blue absolute left-0 w-full bg-transparent focus-visible:outline-2 focus-visible:outline-offset-[3px]"
          />
        ))}

        <div
          ref={tooltipRef}
          aria-hidden="true"
          className={cn(
            "bg-hw-ink text-hw-paper pointer-events-none absolute z-10 w-[260px] -translate-y-1/2 rounded-[10px] px-3 py-2 text-[13px] leading-[1.45] transition-opacity",
            hovered ? "opacity-100" : "opacity-0",
          )}
        >
          {hovered && (
            <>
              <span className="hw-mono text-hw-mist block">
                {trackName(content, hovered.track)} · {answerLevel(hovered.score, content)}
              </span>
              <span className="mt-1 block">“{hovered.evidence}”</span>
            </>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-4">
        <span className="hw-mono">Hard-wired</span>
        <span className="hw-mono">AI-first by default</span>
      </div>

      <figcaption className="sr-only" id={summaryId}>
        <ul>
          {answers.map((answer, index) => (
            <li key={answer.qid}>
              {`Band ${index + 1}, ${trackName(content, answer.track)}: ${answerLevel(answer.score, content)}. You said: ${answer.evidence ?? ""}`}
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  );
}
